import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ComponentRef,
  ViewContainerRef,
  ComponentFactoryResolver
  , TemplateRef, HostListener, Renderer2, ElementRef, inject
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, finalize, map, retry } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import {
  DrawerComponent,
  DrawerMode,
  DrawerPosition,
  DrawerSelectEvent,
  DrawerTemplateDirective
} from '@progress/kendo-angular-layout';
import { BreadCrumbItem } from '@progress/kendo-angular-navigation';
import {
  ActionsLayout,
  DialogRef,
  DialogService
} from '@progress/kendo-angular-dialog';
import { SideMenuItemDto, MenuItemsService, UsersService } from '@api-client';
import { GoogleMapsDrawerArgs } from '@app/shared/components/drawer-forms/google-maps-drawer';
import { ExpenseAddDrawerComponent, ExpenseLinkDrawerComponent } from '@app/accounting/expenses/shared/components';
import { ProductInfoDrawerCommand } from '@app/shared/models/drawer/product';
import { UserHelperService } from '@app/shared/services/data-helper';

import { ViewportService } from '../../shared/services';
import {
  CurrencyRatePayload,
  SideBarDataSharingService,
  SideBarMenuUpdaterService,
  UnitRatePayload
} from '../../shared/services/side-bar';
import {
  AccountAddDrawerComponent,
  CurrencyRateAddDrawerComponent,
  CurrencyInfoAddDrawerComponent,
  UnitAddDrawerComponent,
  InvoicePaymentAddDrawerComponent,
  InvoiceReceiptAddDrawerComponent,
  ManufacturingOrderAddDrawerComponent,
  ProductInfoDrawerComponent,
  GoogleMapsDrawerComponent,
  UnitRateAddDrawerComponent,
  CustomerCommunicationFormDrawerComponent,
  ActivityLogsDrawerComponent,
  DebitCreditMatchingDrawerComponent
} from '../../shared/components/drawer-forms';
import { CurrencyInfo } from '../../shared/models';
import { SalesOrderAddComponent } from '../../sales/sales-orders/sales-order-add/sales-order-add.component';
import { PageTitleService } from '../../shared/services/page-title.service';
import { JournalAddComponent } from '../../accounting/journals/journal-add/journal-add.component';
import { AppSettingsService, EnvConfigLoaderService } from '../../shared/services/app';
import { ContactAddComponent } from '../../contacts/contact-add/contact-add.component';
import { IssuePaymentAddComponent } from '../../accounting/issue-payments/issue-payment-add/issue-payment-add.component';
import { IssueReceiptsAddComponent } from '../../accounting/issue-receipts/issue-receipts-add/issue-receipts-add.component';
import { AddressFormDrawerComponent } from '../../contacts/address';
import { AddressViewDrawerComponent } from '../../contacts/address/address-view-drawer/address-view-drawer.component';
import { CreateAddressDrawerCommand } from '../../shared/models/drawer/address';
import { CreateInvoicePaymentDrawerCommand } from '../../shared/models/drawer/payment';
import { CreateInvoiceReceiptDrawerCommand } from '../../shared/models/drawer/receipt';
import { TranslateService } from '@ngx-translate/core';
import {
  InboxNotification,
  JobNotification,
  JobNotificationsService,
  JobProgress,
  LanguageDirectionService,
  SignalRConnectionService,
  UserNotificationsService
} from '@app/shared/services';
import { MessagingService } from '@app/shared/services/messaging/messaging.service';
import { PendingApprovalsCountService } from '@app/shared/services/approvals/pending-approvals-count.service';
import { ModuleScopeService } from '@app/shared/services/modules/module-scope.service';
import { LaunchpadSearchService } from '@app/launchpad/launchpad-search.service';
import { isChangePasswordUrl } from '@app/core/change-password/change-password.route';
import { AuthenticationService } from '@app/auth/authentication.service';
import { zeymoUserId } from '@app/auth/auth-mode';
import { logoutRequest } from '../../auth/auth-mode';

@Component({
    selector: 'app-shell',
    templateUrl: './shell.component.html',
    styleUrls: ['./shell.component.scss'],
    standalone: false
})
export class ShellComponent implements OnInit, OnDestroy {
  /** Narrows the sidebar to one module when the tab was opened from the launchpad (ZEY-2419). */
  private readonly moduleScope = inject(ModuleScopeService);
  private readonly authService = inject(AuthenticationService);

  /** The launchpad is the shell's home: same header, no sidebar (ZEY-2419). */
  isLaunchpad = false;
  /** /change-password (ZEY-3140) is reached from an email, not the menu: header, no sidebar, like the launchpad. */
  isChangePassword = false;

  /** The launchpad and /change-password show the header's brand and no sidebar. */
  get hidesSidebar(): boolean {
    return this.isLaunchpad || this.isChangePassword;
  }
  /** The launchpad's module filter; its box is in this header. */
  readonly launchpadSearch = inject(LaunchpadSearchService);
  @ViewChild('drawer') drawer: DrawerComponent;
  @ViewChild('dialogActions') dialogActions: TemplateRef<any>;
  @ViewChild('componentContainer', { read: ViewContainerRef, static: true })
  componentContainer: ViewContainerRef;
  @ViewChild('componentContainerDrawer')
  componentContainerDrawer: DrawerComponent;
  @ViewChild(DrawerTemplateDirective, { static: true })
  directive!: DrawerTemplateDirective;
  @ViewChild('notifAnchorShell', { static: false }) notifAnchorShell: ElementRef;

  show = false;
  expandMode: DrawerMode = 'overlay';
  /** 'end' places the drawer on the right for Arabic RTL. */
  public drawerPosition: DrawerPosition = 'start';
  public isRtl = false;
  isMenuShown: boolean;
  isSettingsLoaded = false;
  currentWindowWidth: number;
  subscriptions: Subscription[] = [];
  isOpened = true;
  dropdownButtonData = [
    'Sales Order',
    'Purchase Order',
    'Journal Voucher',
    'Contact',
    'Payment',
    'Receipt'
  ]; //'Add Currency Rate',
  layout: ActionsLayout = 'center';
  accountName: string;
  componentRef: ComponentRef<any>;
  isComponentShown = false;

  envAndTenantName: string;

  public item: any = {};
  public drawerItems: Array<any>;
  dialogRef: DialogRef;
  componentInstance: any;

  // Job notifications (from SignalR) shown in the app bar.
  notifications: JobNotification[] = [];
  lastProgress: JobProgress | null = null;
  isNotificationsPopupOpen = false;

  // Persisted user-inbox notifications (Notifications API + SignalR merge).
  inboxNotifications: InboxNotification[] = [];
  unreadCount = 0;

  // User-to-user messaging (ZEY-2259): the badge on the Messages button.
  messagesUnread = 0;
  messagingEnabled = true;

  // Approvals waiting on this user (ZEY-2462): the badge on the Approvals button.
  pendingApprovalsCount = 0;

  skeletons = [];
  menuItems: SideMenuItemDto[];

  constructor(
    private router: Router,
    private renderer2: Renderer2,
    private viewportService: ViewportService,
    private dialogService: DialogService,
    protected sideBarDataSharingService: SideBarDataSharingService,
    private resolver: ComponentFactoryResolver,
    private pageTitleService: PageTitleService,
    private appSettingsService: AppSettingsService,
    private menuItemsService: MenuItemsService,
    protected sideBarMenuUpdaterService: SideBarMenuUpdaterService,
    private msalService: MsalService,
    private envConfigLoaderService: EnvConfigLoaderService,
    private usersService: UsersService,
    private userHelperService: UserHelperService,
    public translate: TranslateService,
    private jobNotificationsService: JobNotificationsService,
    private signalRConnectionService: SignalRConnectionService,
    private userNotificationsService: UserNotificationsService,
    private messagingService: MessagingService,
    private pendingApprovalsCountService: PendingApprovalsCountService,
    private languageDirection: LanguageDirectionService
  ) {
    this.translate.setDefaultLang('en');
  }

  ngOnInit(): void {
    const subRtl = this.languageDirection.isRtl$.subscribe((isRtl) => {
      this.isRtl = isRtl;
      // Keep position='end' for Arabic so Kendo's host classes match a right rail.
      // CSS below also forces the rail right even if Kendo mirrors start/end under RTL.
      this.drawerPosition = isRtl ? 'end' : 'start';
    });
    this.subscriptions.push(subRtl);

    this.sideBarMenuUpdaterService.setMenuItemsLoading(true);
    // Create skeletons for side menu items
    const viewportHeight = window.innerHeight - 120;
    const skeletonCount = Math.floor(viewportHeight / 55);
    const pattern = [50, 75, 100];
    this.skeletons = Array.from({ length: skeletonCount },
      (_, i) => `${pattern[i % pattern.length]}%`
    );

    const sub1 = this.menuItemsService.menuItemsGet()
      .pipe(
      retry({ count: 2, delay: 1500 }),
      map((x) => {
        // Sorting menu items by insertionOrder
        x.items = x.items?.sort(
          (a, b) => a.insertionOrder - b.insertionOrder
        );

        // Sorting sub menu items by insertionOrder
        x.items = x.items.map(item => {
          item.children = item.children?.sort(
            (a, b) => a.insertionOrder - b.insertionOrder
          );
          return item;
        });

        return x.items;
      }),
      finalize(() => {
        this.sideBarMenuUpdaterService.setMenuItemsLoading(false);
      })
      )
      .subscribe({
        next: res => {
          if (res) {
            this.menuItems = res;
            // A scope left over from a module this user can no longer see falls back to the full menu.
            if (this.moduleScope.scope && !this.scopedMenuItem()) {
              this.moduleScope.clear();
            }
            this.rebuildDrawerItems();
          }
        },
        error: err => {
          // Without this handler a failed menu API call left drawerItems
          // undefined, producing a permanently empty sidebar with no
          // feedback to the user (see Usersnap report 'system not working').
          console.error('Failed to load menu items (after retries):', err);
          this.menuItems = [];
          this.drawerItems = [];
        }
      });
    this.subscriptions.push(sub1);

    // /app/:key (or the launchpad) changing the module scope rebuilds the sidebar.
    this.subscriptions.push(this.moduleScope.scope$.subscribe(() => this.rebuildDrawerItems()));

    this.isLaunchpad = this.isLaunchpadUrl(this.router.url);
    this.isChangePassword = isChangePasswordUrl(this.router.url);
    this.subscriptions.push(this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        this.isLaunchpad = this.isLaunchpadUrl(e.urlAfterRedirects);
        this.isChangePassword = isChangePasswordUrl(e.urlAfterRedirects);
      }));
    
    // Clear warnings of menu items
    const sub2 = this.sideBarMenuUpdaterService.warningsClearer$
      .subscribe(() => {
        this.menuItems = this.menuItems.map(item => {
          item.children = item.children.map(child => {
            child.hasWarnings = false;
            return child;
          });
          item.hasWarnings = false;
          return item;
        });
        this.rebuildDrawerItems();
      });
    this.subscriptions.push(sub2);

    this.pageTitleService.load();
    localStorage.setItem('isMenuOpened', this.isOpened.toString());

    this.getCurrentUserInfo();

    const sub3 = this.sideBarDataSharingService.openCreateCurrencyRateDrawer$.subscribe(
      (currencyRatePayload: CurrencyRatePayload) => {
        this.createDrawerComponent(CurrencyRateAddDrawerComponent);
        this.componentRef.instance.currencyUid =
          currencyRatePayload.currencyUid;
        this.componentRef.instance.mode = currencyRatePayload.mode;
        this.componentRef.instance.defaultRateDirectionType = currencyRatePayload.defaultRateDirectionType;
        this.componentRef.instance.isSideDrawerOpened = true;
        if (currencyRatePayload.mode === 'edit') {
          this.componentRef.instance.currencyRate =
            currencyRatePayload.currencyRate;
        }
      }
    );
    this.subscriptions.push(sub3);

    const sub4 = this.sideBarDataSharingService.openCurrencyInfoAddDrawer$.subscribe(
      (currency: CurrencyInfo) => {
        this.createDrawerComponent(CurrencyInfoAddDrawerComponent);
        this.componentRef.instance.currency = currency;
        this.componentRef.instance.mode = currency?.mode;
      }
    );
    this.subscriptions.push(sub4);

    const sub6 = this.sideBarDataSharingService.sendCloseMessage$.subscribe((data) => {
      this.componentRef.instance.drawer.toggle(false);
      setTimeout(() => {
        this.componentRef =
          this.sideBarDataSharingService.components[
          this.sideBarDataSharingService.activeComponentsCount - 1
          ];

      }, 351);
      if (data) {
        this.sideBarDataSharingService.sendDataOnClose(data);
      }
    });
    this.subscriptions.push(sub6);

    const sub7 = this.sideBarDataSharingService.openCreateAccountDrawer$.subscribe(
      (accountName) => {
        // this.accountName = accountName;
        this.createDrawerComponent(AccountAddDrawerComponent);
        this.componentRef.instance.accountName = accountName;
      }
    );
    this.subscriptions.push(sub7);

    const sub8 = this.sideBarDataSharingService.openCreateAddressDrawer$.subscribe(
      (data?: CreateAddressDrawerCommand) => {
        this.createDrawerComponent(AddressFormDrawerComponent);
        this.componentRef.instance.data = data;
      }
    );
    this.subscriptions.push(sub8);

    const sub9 = this.sideBarDataSharingService.openViewAddressDrawer$.subscribe(
      (uid?: string) => {
        this.createDrawerComponent(AddressViewDrawerComponent);
        this.componentRef.instance.addressUid = uid;
      }
    );
    this.subscriptions.push(sub9);

    const sub10 = this.sideBarDataSharingService.openCreateUnitDrawer$.subscribe(
      (name) => {
        this.createDrawerComponent(UnitAddDrawerComponent);
        // Sending the desired Unit name to UnitAddDrawerComponent
        this.componentRef.instance.name = name;
      }
    );
    this.subscriptions.push(sub10);

    const sub19 = this.sideBarDataSharingService.openCreateUnitRateDrawer$.subscribe(
      (unitRatePayload: UnitRatePayload) => {
        this.createDrawerComponent(UnitRateAddDrawerComponent);
        this.componentRef.instance.unitRatePayload = unitRatePayload;
      }
    );
    this.subscriptions.push(sub19);

    const sub11 = this.sideBarDataSharingService.openCreateExpenseDrawer$.subscribe(
      (invoiceUid) => {
        this.createDrawerComponent(ExpenseAddDrawerComponent);
        // Sending the desired invoiceUid to ExpenseAddDrawerComponent
        this.componentRef.instance.invoiceUid = invoiceUid;
      }
    );
    this.subscriptions.push(sub11);

    const sub11b = this.sideBarDataSharingService.openLinkExpenseDrawer$.subscribe(
      (invoiceUid) => {
        this.createDrawerComponent(ExpenseLinkDrawerComponent);
        this.componentRef.instance.invoiceUid = invoiceUid;
      }
    );
    this.subscriptions.push(sub11b);

    const sub12 = this.sideBarDataSharingService.openCreateInvoicePaymentDrawer$.subscribe(
      (result: CreateInvoicePaymentDrawerCommand) => {
        this.createDrawerComponent(InvoicePaymentAddDrawerComponent);
        this.componentRef.instance.invoicePaymentData = result;
      }
    );
    this.subscriptions.push(sub12);

    const sub13 = this.sideBarDataSharingService.openCreateInvoiceReceiptDrawer$.subscribe(
      (result: CreateInvoiceReceiptDrawerCommand) => {
        this.createDrawerComponent(InvoiceReceiptAddDrawerComponent);
        this.componentRef.instance.invoiceReceiptData = result;
      }
    );
    this.subscriptions.push(sub13);

    const sub16 = this.sideBarDataSharingService.openProductInfoDrawer$.subscribe(
      (data: ProductInfoDrawerCommand) => {
        this.createDrawerComponent(ProductInfoDrawerComponent);
        this.componentRef.instance.data = data;
      }
    );
    this.subscriptions.push(sub16);

    const sub17 = this.sideBarDataSharingService.openCreateManufacturingOrderDrawer$.subscribe(
      (productUid: string) => {
        this.createDrawerComponent(ManufacturingOrderAddDrawerComponent);
        this.componentRef.instance.productUid = productUid;
      }
    );
    this.subscriptions.push(sub17);

    const sub18 = this.sideBarDataSharingService.openGoogleMapsDrawer$.subscribe(
      (args: GoogleMapsDrawerArgs) => {
        this.createDrawerComponent(GoogleMapsDrawerComponent);
        this.componentRef.instance.data = args;
      }
    );
    this.subscriptions.push(sub18);

    const sub20 = this.sideBarDataSharingService.openCommunicationFormDrawer$.subscribe(
      (args) => {
        this.createDrawerComponent(CustomerCommunicationFormDrawerComponent);
        this.componentRef.instance.contactUid = args;
      }
    );
    this.subscriptions.push(sub20);

    const sub21 = this.sideBarDataSharingService.openActivityLogsDrawer$.subscribe(
      (args) => {
        this.createDrawerComponent(ActivityLogsDrawerComponent);
        this.componentRef.instance.data = args;
      }
    );
    this.subscriptions.push(sub21);

    const sub22 = this.sideBarDataSharingService.openDebitCreditMatchingDrawer$.subscribe(
      (args) => {
        this.createDrawerComponent(DebitCreditMatchingDrawerComponent);
        this.componentRef.instance.data = args;
      }
    );
    this.subscriptions.push(sub22);

    const sub14 = this.viewportService.isMenuShown$.subscribe(
      (isMenuShown) => {
        this.isMenuShown = isMenuShown;
        this.isOpened = this.isMenuShown;

        if (isMenuShown) {
          this.expandMode = 'push';
          this.viewportService.sendOpenMenuMessage(this.isOpened);
        } else {
          this.expandMode = 'overlay';
          this.viewportService.sendOpenMenuMessage(this.isOpened);
        }
      }
    );
    this.subscriptions.push(sub14);

    const sub15 = this.appSettingsService.isSettingsLoaded$.subscribe((res) => {
        if (res) {
          this.isSettingsLoaded = res;
        }
      });
    this.subscriptions.push(sub15);

    // Subscribe to shared job notifications for the app bar.
    const subNotifications = this.jobNotificationsService.notifications$.subscribe(list => {
      this.notifications = list;
    });
    const subProgress = this.jobNotificationsService.lastProgress$.subscribe(progress => {
      this.lastProgress = progress;
    });
    const subInbox = this.userNotificationsService.inbox$.subscribe(list => {
      this.inboxNotifications = list;
    });
    const subUnread = this.userNotificationsService.unreadCount$.subscribe(count => {
      this.unreadCount = count;
    });
    this.subscriptions.push(subNotifications, subProgress, subInbox, subUnread);

    const subMessagesUnread = this.messagingService.unread$.subscribe(count => {
      this.messagesUnread = count;
    });
    const subMessagingEnabled = this.messagingService.enabled$.subscribe(enabled => {
      this.messagingEnabled = enabled;
    });
    this.subscriptions.push(subMessagesUnread, subMessagingEnabled);

    this.subscriptions.push(this.pendingApprovalsCountService.count$.subscribe(count => {
      this.pendingApprovalsCount = count;
    }));
  }

  get isMenuItemsLoading() {
    return this.sideBarMenuUpdaterService.isMenuItemsLoading;
  }

  private async getCurrentUserInfo() {
    const response = await this.usersService.usersCurrentUserGet().toPromise();
    const user = response.user;
    // Actively apply the signed-in user's own language. ngx-translate's
    // currentLang is a process-global singleton that survives in-SPA user
    // switches / bfcache restores, and setDefaultLang() only sets the
    // missing-key fallback — it cannot override a currentLang left active by a
    // previous user (e.g. one who picked French in the profile dropdown, which
    // calls translate.use()). Using use() with an explicit 'en' fallback resets
    // the active language to this user's own preference on every login, so the
    // language can never leak across users. (ZEY-299)
    // LanguageDirectionService (root) listens to onLangChange and flips
    // document dir for Arabic RTL vs LTR languages.
    this.translate.use(user.language || 'en');
    this.userHelperService.setData(response.user);
    const envName = this.envConfigLoaderService.getEnvName();
    if (envName !== 'prd') {
      this.envAndTenantName = `${envName}-${response.user?.tenantId}`;
    }

    // Subscribe to user-specific notification topic for real-time push
    const notificationsUserId = this.notificationsUserId(user.uid);
    if (notificationsUserId) {
      this.signalRConnectionService.subscribeToTopic(`user-notifications:${notificationsUserId}`);
    }

    // Load the persisted unread inbox for the bell.
    this.userNotificationsService.load();

    // And the conversations behind the Messages badge.
    this.messagingService.load();

    // And the approvals waiting on this user.
    this.pendingApprovalsCountService.load();
  }

  /**
   * The id behind this user's `user-notifications:` topic. messaging-svc and ZeymoAPI's negotiate
   * step authorise the token's subject (the B2C object id, `User.AzureUid`), which differs from
   * `User.Uid` on restored tenants — so it is read from the MSAL account, not the current-user DTO.
   * Under External ID that id is the token's `extension_LegacyUid` (ZEY-3138).
   */
  private notificationsUserId(userUid?: string): string | undefined {
    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];
    const subject = zeymoUserId(this.authService.getApiTokenClaims(), account?.idTokenClaims, account?.localAccountId);
    return subject || userUid;
  }

  /** Where the Approvals button goes: the My Approvals menu entry, else the hub's home. */
  get myApprovalsPath(): string {
    const myApprovals = (this.menuItems ?? [])
      .flatMap(item => item.children ?? [])
      .find(child => child.uid === 'approvals-my');
    return myApprovals?.path || '/approvals';
  }

  get visibleJobNotifications(): JobNotification[] {
    return this.notifications.filter(
      n => n.messageType !== 'info' && n.messageType !== 'progress' && n.messageType !== 'notification'
    );
  }

  get bellCount(): number {
    return this.unreadCount + this.visibleJobNotifications.length;
  }

  markNotificationRead(notification: InboxNotification): void {
    this.userNotificationsService.markRead(notification.uid);
  }

  markAllNotificationsRead(): void {
    this.userNotificationsService.markAllRead();
  }

  toggleNotificationsPopup(): void {
    this.isNotificationsPopupOpen = !this.isNotificationsPopupOpen;
  }

  clearJobNotifications(): void {
    this.jobNotificationsService.clearNotifications();
  }

  openCreationDialog(
    title: string,
    width: number,
    component: any,
    navigation = false,
    addRoute = ''
  ) {
    if (navigation) {
      this.router.navigate(['/' + addRoute + '/add'], {});
    } else {
      this.dialogRef = this.dialogService.open({
        title: title,
        content: component,
        width: width,
        actions: this.dialogActions,
        actionsLayout: this.layout
      });

      this.componentInstance = this.dialogRef.content.instance;
      this.componentInstance.isInDialog = true;
    }
  }

  onSubmit() {
    if (this.componentInstance) {
      this.componentInstance.submitRecord();
      if (this.componentInstance?.form?.invalid) {
        return;
      }
    }
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  closeDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  onDropdownButtonItemClick(title: string) {
    switch (title) {
      case 'Sales Order':
        this.openCreationDialog(
          title,
          1280,
          SalesOrderAddComponent,
          true,
          'sales-orders'
        );
        break;
      case 'Purchase Order':
        this.openCreationDialog(
          title,
          1280,
          SalesOrderAddComponent,
          true,
          'purchase-orders'
        );
        break;
      case 'Journal Voucher':
        this.openCreationDialog(
          title,
          1280,
          JournalAddComponent,
          true,
          'journals'
        );
        break;
      case 'Contact':
        this.openCreationDialog(
          title,
          1280,
          ContactAddComponent,
          true,
          'contacts'
        );
        break;
      case 'Payment':
        this.openCreationDialog(
          title,
          1280,
          IssuePaymentAddComponent,
          true,
          'issue-payments'
        );
        break;
      case 'Receipt':
        this.openCreationDialog(
          title,
          1280,
          IssueReceiptsAddComponent,
          true,
          'issue-receipts'
        );
        break;
    }
  }

  toggleDrawer() {
    this.isOpened = !this.drawer.expanded;//!this.isOpened;
    this.viewportService.sendOpenMenuMessage(this.isOpened);

    if (this.expandMode === 'push') {
      localStorage.setItem('isMenuOpened', this.isOpened.toString());
    }
    this.drawer.toggle(this.isOpened);
  }

  public onItemClick(item: BreadCrumbItem): void {
    this.router.navigate(['/' + item.title.toLowerCase()]);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  createDrawerComponent(component: any) {
    const factory = this.resolver.resolveComponentFactory(component);
    this.componentRef = this.componentContainer.createComponent(factory);
    this.renderer2.addClass(this.componentRef.location.nativeElement, 'side-drawer');
    this.sideBarDataSharingService.addComponent(this.componentRef);
    this.componentRef.instance.index =
      this.sideBarDataSharingService.activeComponentsCount;
    this.sideBarDataSharingService.sendTotalNumber(
      this.sideBarDataSharingService.activeComponentsCount
    );
    setTimeout(() => {
      this.componentRef.instance.drawer.toggle(true);
      this.sideBarDataSharingService.sendIsDrawerOpenedNotification(true);
    }, 0);
  }

  private setDrawerSelectedItem(): void {
    const urlRoute = this.appSettingsService.getUrlRoute();
    const routeElements = this.appSettingsService.getRouteParam(urlRoute);
    const route = routeElements
      .join('/')
      // Replace any kind of sub paths with empty string to get only the parent route
      .replace(/\/[\w-]+(?:\/[\w-]+)*/g, '');
    if (!this.drawerItems) {
      return;
    }
    const node = this.drawerItems.find(x => {
      // Checking if current item is a parent
      if (x.children && x.children?.length > 0) {
        // finding out the child node element under parent
        if (x.children.find((i: SideMenuItemDto) => i.path === `/${route}`)) {
          return x;
        }
        
        // if (routeElements[0] === 'credit-notes') {
          // if () {
            // console.log('returning children: ', x.children.find((i) => i.name === 'Sales Invoices'));
            // return x.children.find((i) => i.name === 'Sales Invoices');
          // }
        // }
      // Otherwise checking if current item path is matching with the URL 
      } else if (x.path === `/${route}`) {
        return x;
      }
    })
      // Fallback for iframe-hosted modules whose menu items live one level deeper
      // (e.g. /accounting/journal-vouchers, /manufacturing/orders): the exact match
      // above only ever sees the first segment, so match on the prefix instead.
      ?? this.drawerItems.find(x =>
        x.children?.some((i: SideMenuItemDto) => i.path?.startsWith(`/${route}/`))
      );
    if (!node) {
      return;
    }
    // console.log('node is: ', node);

    const nodeIndex = this.drawerItems.findIndex((i) => i.name === node.name);
    this.onSelect({ index: nodeIndex, item: this.drawerItems[nodeIndex] } as DrawerSelectEvent, true);
  }

  /** Rebuilds the sidebar from the visible menu (the scoped module, or the full tree). */
  private rebuildDrawerItems(): void {
    if (!this.menuItems) {
      return;
    }

    this.drawerItems = this.resetItems();
    this.setDrawerSelectedItem();
    // A module opened on its own shows its screens straight away.
    const [only] = this.drawerItems;
    if (this.isModuleScoped && this.drawerItems.length === 1 && only?.isParent && !only.expanded) {
      this.onSelect({ index: 0, item: only } as DrawerSelectEvent, false);
    }
  }

  public onSelect(ev: DrawerSelectEvent, parentSelection = true): void {
    this.item = ev.item;
    const name = ev.item.name;

    if (!this.item.isParent) {
      //this.router.navigate([this.item.path]);
      return;
    }

    const newItems = this.resetItems();
    const index = newItems.findIndex((i) => i.name === name);

    if (parentSelection) {
      newItems[index].selected = true;
    }

    if (!this.item.expanded) {
      newItems[index].expanded = true;
      this.addChildren(newItems, index, newItems[index].children);
    }

    this.drawerItems = newItems;
  }

  public addChildren(arr, index, children: Array<any>) {
    arr.splice(index + 1, 0, ...children);
  }

  private isLaunchpadUrl(url: string): boolean {
    // The launchpad is the root (ZEY-2925).
    const path = url.split(/[?#]/)[0];
    return path === '/' || path === '';
  }

  /** True while this tab's sidebar is narrowed to one module (ZEY-2419). */
  public get isModuleScoped(): boolean {
    return !!this.scopedMenuItem();
  }

  private scopedMenuItem(): SideMenuItemDto | undefined {
    const uid = this.moduleScope.scope?.toLowerCase();
    return uid ? this.menuItems?.find(item => item.uid?.toLowerCase() === uid) : undefined;
  }

  /** The menu the sidebar shows: the scoped module alone, else everything. */
  private get visibleMenuItems(): SideMenuItemDto[] | undefined {
    const scoped = this.scopedMenuItem();
    return scoped ? [scoped] : this.menuItems;
  }

  public resetItems(): Array<any> {
    const arr = [];
    (this.visibleMenuItems ?? []).forEach((parent) => {
      if (parent?.children?.length) {
        parent.children.forEach((child) => {
          child['parentuid'] = parent.uid;
        });
      }
      arr.push(Object.assign({}, parent));
    });
    return arr;
  }

  logout() {
    this.msalService.logout(logoutRequest(this.msalService.instance));
  }
  
  @HostListener('document:keydown.escape') onEscapeHandler(): void {
    this.closeDialog();
  }

  @HostListener('click', ['$event.target']) public onClick(targetElement) {

    if (targetElement.classList.contains('k-overlay')) {
      if (this.drawer.expanded && this.drawer.mode === 'overlay') {
        this.toggleDrawer();
      }
    }

    // Close notification popup when clicking outside
    if (this.isNotificationsPopupOpen && 
        !targetElement.closest('.notif-bell') && 
        !targetElement.closest('.notif-popup') &&
        !targetElement.closest('.notif-container')) {
      this.isNotificationsPopupOpen = false;
    }
  }
}
